import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeStore, type ThemeMode } from '@/store/theme.js';
import { usePlayerStore } from '@/store/player.js';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';
import { gameRoute } from '@/platform/games.js';

interface PublicRoom {
  roomId: string;
  gameId: string;
  gameName: string;
  playerCount: number;
  createdAt: number;
}

const AVATAR_OPTIONS = ['🎮','🎯','🎲','🃏','🏆','🦊','🐧','🦁','🐸','🐙','🦄','🤖','👾','🦅','🐺','🎭','🎪','🔥','⚡','🌊'];

const MODES: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
  {
    value: 'light',
    label: 'Light',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    ),
  },
  {
    value: 'auto',
    label: 'Auto',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
];

export function BurgerMenu() {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [siteName, setSiteName] = useState('Playground');
  const [aiBuilderEnabled, setAiBuilderEnabled] = useState(true);
  const [publicRooms, setPublicRooms] = useState<PublicRoom[]>([]);
  const [allGames, setAllGames] = useState<{ id: string; name: string }[]>([]);
  const { mode, setMode, menuSide, setMenuSide } = useThemeStore();
  const { displayName, avatar, setDisplayName, setAvatar, credits, favorites, toggleFavorite } = usePlayerStore();
  const navigate = useNavigate();

  const asideRef = useRef<HTMLElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);

  // Profile edit state
  const [editName, setEditName] = useState('');
  const [editAvatar, setEditAvatar] = useState('🎮');

  useEffect(() => {
    const token = usePlayerStore.getState().token;
    if (!token) return;
    apiFetchWithToken('/api/games', token)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { siteName?: string; aiBuilderEnabled?: boolean; games?: { id: string; name: string }[] } | null) => {
        if (!d) return;
        if (d.siteName) setSiteName(d.siteName);
        setAiBuilderEnabled(d.aiBuilderEnabled !== false);
        if (d.games) setAllGames(d.games);
      })
      .catch(() => {});
  }, []);

  // Fetch public rooms whenever sidebar opens, then poll every 5 s while open
  useEffect(() => {
    if (!open) return;
    const fetchRooms = () =>
      apiFetch('/api/rooms/public', { cache: 'no-store' })
        .then((r) => r.json() as Promise<{ rooms: PublicRoom[] }>)
        .then((d) => setPublicRooms(d.rooms ?? []))
        .catch(() => setPublicRooms([]));
    fetchRooms();
    const id = setInterval(fetchRooms, 5000);
    return () => clearInterval(id);
  }, [open]);

  // Read through a ref so toggling the profile modal doesn't tear down and
  // re-arm the focus trap (which would yank focus back to the burger button).
  const profileOpenRef = useRef(profileOpen);
  profileOpenRef.current = profileOpen;

  // The drawer stays mounted while closed so it can slide out. Without `inert`
  // its buttons stay Tab-reachable off-screen. Set imperatively: React 18 does
  // not recognise `inert` as a JSX prop.
  useEffect(() => {
    asideRef.current?.toggleAttribute('inert', !open);
  }, [open]);

  // Escape closes, Tab is trapped inside the drawer, focus starts on the panel
  // and returns to the burger button on close. Background scroll is locked.
  useEffect(() => {
    if (!open) return;

    const aside = asideRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    function focusables(): HTMLElement[] {
      if (!aside) return [];
      return Array.from(
        aside.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (profileOpenRef.current) setProfileOpen(false);
        else setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !aside?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKey);

    // Defer so the drawer has finished its enter transition and is focusable.
    const raf = requestAnimationFrame(() => focusables()[0]?.focus());

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;

      // Restore focus to whatever opened the drawer. `document.activeElement`
      // is <body> when the drawer was opened without focusing the trigger, and
      // the node may have unmounted — either way, focus must land on the
      // burger rather than being dropped to the document.
      const restoreTarget =
        previouslyFocused &&
        previouslyFocused !== document.body &&
        previouslyFocused.isConnected
          ? previouslyFocused
          : burgerRef.current;
      restoreTarget?.focus();
    };
  }, [open]);

  function openProfile() {
    setEditName(displayName);
    setEditAvatar(avatar);
    setProfileOpen(true);
  }

  function saveProfile() {
    if (editName.trim()) setDisplayName(editName.trim());
    setAvatar(editAvatar);
    setProfileOpen(false);
  }

  return (
    <>
      {/* Burger button — fixed, clear of the iOS home indicator */}
      <button
        ref={burgerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="app-menu"
        style={{
          bottom: 'max(1rem, var(--safe-b))',
          [menuSide === 'right' ? 'right' : 'left']:
            menuSide === 'right' ? 'max(1rem, var(--safe-r))' : 'max(1rem, var(--safe-l))',
        }}
        className={[
          'fixed z-50 flex h-10 w-10 items-center justify-center rounded-full border shadow-md transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
          open
            ? 'border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
        ].join(' ')}
      >
        {open ? (
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </button>

      {/* Backdrop */}
      {open && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
          onClick={() => { setOpen(false); setProfileOpen(false); }}
        />
      )}

      {/* Sidebar. `inert` while closed: it stays mounted for the slide
          transition, and without this its controls remain Tab-reachable
          off-screen. */}
      <aside
        ref={asideRef}
        id="app-menu"
        role="dialog"
        aria-modal={open || undefined}
        aria-label="Main menu"
        className={[
          `fixed ${menuSide === 'right' ? 'right-0' : 'left-0'} top-0 bottom-0 z-40 flex w-64 flex-col`,
          `bg-white dark:bg-gray-800 ${menuSide === 'right' ? 'border-l' : 'border-r'} border-gray-200 dark:border-gray-700 shadow-2xl`,
          'transition-transform duration-200 ease-out motion-reduce:transition-none',
          open ? 'translate-x-0' : menuSide === 'right' ? 'translate-x-full' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Site name + X close button */}
        <div className="px-5 pt-[calc(1.5rem+var(--safe-t))] pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <img src="/favicon.png" alt="" className="w-8 h-8 rounded-lg flex-shrink-0" />
            <p className="text-xl font-extrabold tracking-tight text-indigo-700 dark:text-indigo-400 flex-1 truncate">
              {siteName}
            </p>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="ml-1 flex h-7 w-7 items-center justify-center rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Credits */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="text-base">💰</span>
            <span className="text-sm font-semibold tabular-nums text-gray-700 dark:text-gray-300">
              {credits} credits
            </span>
          </div>
        </div>

        {/* Nav links */}
        <div className="px-3 pt-3 space-y-0.5">
          <button
            onClick={() => { setOpen(false); void navigate('/'); }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Home
          </button>
          <button
            onClick={() => { setOpen(false); void navigate('/leaderboard'); }}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Leaderboard
          </button>
          {aiBuilderEnabled && (
            <button
              onClick={() => { setOpen(false); void navigate('/build'); }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9l3 3-3 3" /><line x1="13" y1="15" x2="17" y2="15" />
              </svg>
              Build a Game
            </button>
          )}
        </div>

        {/* Favorites */}
        {favorites.length > 0 && (
          <div className="px-3 pt-4 pb-1 border-t border-gray-100 dark:border-gray-700">
            <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Favorites
            </p>
            {/* Two sibling buttons in a flex row — a remove button nested
                inside the navigate button is invalid HTML and swallows clicks. */}
            {favorites.map((gameId) => {
              const game = allGames.find((g) => g.id === gameId);
              const label = game?.name ?? gameId;
              return (
                <div
                  key={gameId}
                  className="flex w-full items-center rounded-lg pr-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <button
                    type="button"
                    onClick={() => { setOpen(false); void navigate(gameRoute(gameId)); }}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-300"
                  >
                    <span aria-hidden="true" className="shrink-0 text-base">❤️</span>
                    <span className="flex-1 truncate text-left">{label}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(gameId)}
                    aria-label={`Remove ${label} from favorites`}
                    className="shrink-0 rounded-md px-1.5 py-1 text-gray-400 transition-colors hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-500 dark:hover:text-red-400"
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Public rooms */}
        <div className="flex min-h-0 flex-1 flex-col pt-4">
          <p className="mb-2 shrink-0 px-6 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Open Rooms
          </p>
          <div className="min-h-0 flex-1 overflow-y-auto px-3">
            {publicRooms.length === 0 ? (
              <p className="px-3 text-xs text-gray-500 dark:text-gray-400">No open rooms right now.</p>
            ) : (
              <ul className="space-y-1 pb-2">
                {publicRooms.map((room) => (
                  <li key={room.roomId}>
                    <button
                      onClick={() => { setOpen(false); void navigate(`/join?code=${room.roomId}`); }}
                      className="flex w-full flex-col rounded-lg px-3 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-gray-800 dark:text-gray-200 truncate">{room.gameName}</span>
                        <span className="ml-2 shrink-0 text-xs text-gray-500 dark:text-gray-400">{room.playerCount} 👤</span>
                      </div>
                      <span className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(room.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Theme selector */}
        <div className="px-5 pb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Theme
          </p>
          <div className="flex gap-1">
            {MODES.map(({ value, label, icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                aria-pressed={mode === value}
                className={[
                  'flex flex-1 flex-col items-center gap-1 rounded-lg py-2 text-xs font-medium transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  mode === value
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700',
                ].join(' ')}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Menu side toggle */}
        <div className="px-5 pb-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Menu Side
          </p>
          <div className="flex gap-1">
            {(['left', 'right'] as const).map((side) => (
              <button
                key={side}
                type="button"
                onClick={() => setMenuSide(side)}
                aria-pressed={menuSide === side}
                className={[
                  'flex flex-1 items-center justify-center gap-1 rounded-lg py-2 text-xs font-medium transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                  menuSide === side
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700',
                ].join(' ')}
              >
                <span aria-hidden="true">{side === 'left' ? '◀' : '▶'}</span>{' '}
                {side.charAt(0).toUpperCase() + side.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Username — clickable to open profile modal */}
        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={openProfile}
            className="flex w-full items-center gap-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors -mx-1 px-1 py-1"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/40">
              <span className="text-lg">{avatar}</span>
            </div>
            <div className="min-w-0 text-left">
              <span className="block truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                {displayName || 'Anonymous'}
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">Edit profile</span>
            </div>
          </button>
        </div>

        {/* Profile modal — inline overlay */}
        {profileOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-dialog-title"
            className="absolute inset-0 z-50 flex flex-col bg-white dark:bg-gray-800"
          >
            <div className="flex items-center justify-between px-5 pt-[calc(1.25rem+var(--safe-t))] pb-4 border-b border-gray-100 dark:border-gray-700">
              <h2 id="profile-dialog-title" className="text-base font-bold text-gray-900 dark:text-gray-100">Edit Profile</h2>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                aria-label="Close profile editor"
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 dark:text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:bg-gray-700"
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Name input */}
              <div>
                <label htmlFor="profile-display-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Display Name
                </label>
                <input
                  id="profile-display-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={24}
                  placeholder="Your name"
                  autoFocus
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
                />
              </div>

              {/* Avatar picker — a radiogroup, so arrow keys and state are announced. */}
              <div>
                <p id="avatar-picker-label" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Avatar
                </p>
                <div role="radiogroup" aria-labelledby="avatar-picker-label" className="grid grid-cols-5 gap-2">
                  {AVATAR_OPTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      role="radio"
                      aria-checked={editAvatar === emoji}
                      aria-label={`Avatar ${emoji}`}
                      onClick={() => setEditAvatar(emoji)}
                      className={[
                        'flex h-10 w-10 items-center justify-center rounded-lg text-xl transition-colors',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        editAvatar === emoji
                          ? 'bg-indigo-100 ring-2 ring-indigo-500 dark:bg-indigo-900/40'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                      ].join(' ')}
                    >
                      <span aria-hidden="true">{emoji}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-5 pb-5 pt-3 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={saveProfile}
                className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-semibold text-white transition-transform duration-150 ease-out hover:bg-indigo-700 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
